'use client';

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, X, AlertTriangle, RefreshCw } from 'lucide-react';
import { networkState } from '@/lib/networkState';
import { useSyncStatus } from '@/contexts/SyncStatusContext';
import { clearInstalledPwaCaches, clearAllAppData, recoverServiceWorkerAfterCacheReset } from '@/lib/serviceWorkerRegistration';
import { clearNativeCache } from '@/lib/nativeCacheBridge';
import { frontendLogger } from '@/lib/frontendLoggingService';
import { notify } from '@/lib/notifications';

interface ClearCacheModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-check the "Clear Cache & Data" option when the modal opens. */
  initialIncludeData?: boolean;
}

export default function ClearCacheModal({ isOpen, onClose, initialIncludeData = false }: ClearCacheModalProps) {
  const [isClearing, setIsClearing] = useState(false);
  const [includeData, setIncludeData] = useState(initialIncludeData);
  const { stats: syncStats } = useSyncStatus();

  // Reset transient state each time the modal opens (render-phase adjust pattern).
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setIncludeData(initialIncludeData);
      setIsClearing(false);
    }
  }

  const isCurrentlyOnline = networkState.isOnline();
  const pendingClockActions = Math.max(0, syncStats.pendingActions || 0);
  const pendingWorkItems = Math.max(0, syncStats.pendingWorkItems || 0);
  const pendingUploads = Math.max(0, syncStats.pendingWorkItemUploads || 0);
  const hasPendingOfflineData = pendingClockActions > 0 || pendingWorkItems > 0 || pendingUploads > 0;

  const handleClearCache = async () => {
    // Show confirmation dialog about offline data loss
    const warningMessages = [];
    if (!isCurrentlyOnline) {
      warningMessages.push('You are currently offline.');
    }
    if (hasPendingOfflineData) {
      warningMessages.push('You have unsynced work items and clock events that could be lost.');
    }

    const message = warningMessages.length > 0
      ? `${warningMessages.join(' ')} Ensure you are online before clearing cache to prevent data loss.`
      : 'This will clear cached app files.';

    const confirmed = await notify.confirm({
      title: 'Clear Cache Warning',
      message: message,
      confirmText: 'Continue',
      cancelText: 'Cancel',
      variant: 'danger',
      requireConfirmation: {
        type: 'checkbox',
        label: 'I understand this action cannot be undone and I want to proceed.',
      },
    });

    if (!confirmed) {
      return;
    }

    setIsClearing(true);

    try {
      console.log(`[ClearCacheModal] Starting ${includeData ? 'full data' : 'safe cache'} clear...`);

      // Purge Cloudflare edge cache for the current zone so fresh content is served after reload
      try {
        const purgeResponse = await fetch('/api/v1/cdn-cache/purge', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        const purgeData = await purgeResponse.json();
        if (purgeData.success) {
          console.log(`[ClearCacheModal] Cloudflare cache purged for ${purgeData.zoneDomain || 'current zone'}`);
        } else {
          console.warn('[ClearCacheModal] Cloudflare cache purge failed:', purgeData.error);
        }
      } catch (cfError) {
        console.warn('[ClearCacheModal] Cloudflare cache purge error (non-fatal):', cfError);
      }

      let fullResetDiagnostics: Awaited<ReturnType<typeof clearAllAppData>> | null = null;

      if (includeData) {
        fullResetDiagnostics = await clearAllAppData();
      } else {
        await clearInstalledPwaCaches();
        await recoverServiceWorkerAfterCacheReset();
      }

      clearNativeCache();

      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('[ClearCacheModal] Cache clear complete, reloading...');

      if (includeData && fullResetDiagnostics) {
        frontendLogger.info('Full app reset completed', {
          component: 'ClearCacheModal',
          action: 'clear_cache_data',
          resetOutcome: fullResetDiagnostics.outcome,
          indexedDbDeleted: fullResetDiagnostics.indexedDbDeleted,
          indexedDbFailed: fullResetDiagnostics.indexedDbFailed,
          indexedDbRetried: fullResetDiagnostics.indexedDbRetried,
          clearSiteDataRequested: fullResetDiagnostics.clearSiteDataRequested,
        });
      }

      window.location.reload();
    } catch (error) {
      console.error('[ClearCacheModal] Cache clear failed:', error);
      setIsClearing(false);
      notify.error('Failed to clear cache. Please try again.');
    }
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div className="fixed inset-0 z-[100] bg-gray-50 dark:bg-gray-900 overflow-y-auto" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
        {/* Header with close button */}
        <div className="mb-3 sm:mb-6 px-2 relative">
          <div className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-red-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
              <Trash2 className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1">Clear Cache</h1>
          </div>
          <button
            onClick={onClose}
            disabled={isClearing}
            className="absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <main className="grid grid-cols-1 gap-2 px-2 md:grid-cols-2 sm:gap-4">
          {/* Warning Card */}
          <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-2">
                  {isCurrentlyOnline ? 'Clear cached app files?' : 'Clear cache while offline?'}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
                  {isCurrentlyOnline
                    ? 'This will clear the Cloudflare edge cache and remove cached app files. Your account data will be preserved.'
                    : 'Clearing cache while offline may require internet before the app works normally again.'}
                </p>

                {hasPendingOfflineData && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 dark:bg-amber-900/20 dark:border-amber-800">
                    <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1">
                      You have unsynced offline data:
                    </p>
                    <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                      {pendingClockActions > 0 && <li>• {pendingClockActions} pending clock action{pendingClockActions !== 1 ? 's' : ''}</li>}
                      {pendingWorkItems > 0 && <li>• {pendingWorkItems} pending work item{pendingWorkItems !== 1 ? 's' : ''}</li>}
                      {pendingUploads > 0 && <li>• {pendingUploads} pending upload{pendingUploads !== 1 ? 's' : ''}</li>}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Clear Cache Option */}
          <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
            <label className="flex items-start gap-4 cursor-pointer">
              <input
                type="checkbox"
                checked={includeData}
                onChange={(e) => setIncludeData(e.target.checked)}
                disabled={isClearing}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
              />
              <div className="flex-1">
                <div className="font-medium text-slate-900 dark:text-white mb-1">
                  Clear Cache & Data
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  {hasPendingOfflineData
                    ? 'This will permanently remove unsynced offline work items, clock info, uploads, forms, and other local data from this device. You will stay signed in.'
                    : isCurrentlyOnline
                      ? 'This removes all local app data on this device. You will stay signed in.'
                      : 'This removes all local app data on this device and may require internet before the app works normally again.'}
                </div>
              </div>
            </label>
          </div>
        </main>

        {/* Action Buttons */}
        <div className="border-t border-gray-200 bg-gray-50 px-2 py-4 dark:border-gray-700 dark:bg-gray-900/40 sm:px-6 mt-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <button
              onClick={onClose}
              disabled={isClearing}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleClearCache}
              disabled={isClearing}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-600 to-red-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed sm:min-w-[180px]"
            >
              {isClearing ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  {includeData ? 'Clear Cache & Data' : 'Clear Cache'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
}
